<template>
  <div class="mx-auto mw-700">
    <v-spacer class="mt-2" />

    <h3>
      {{ $t('host.history.title') }}
    </h3>

    <v-btn
      v-if="!openedForm"
      @click="openedForm = !openedForm"
      color="primary"
      elevation="0"
      class="mt-5"
    >
      {{ $t('host.history.addObservation') }}
    </v-btn>

    <v-spacer class="my-3" />

    <v-form
      v-if="openedForm"
      @submit.prevent="validate"
      ref="addObservationForm"
      v-model="valid"
    >
      <v-row>
        <v-col cols="12" sm="4">
          <v-select
            v-model="action"
            :items="actions"
            :rules="[rules.required]"
            :label="$t('host.label.action')"
            hide-details="auto"
            outlined
          />
        </v-col>
      </v-row>

      <v-spacer class="my-3" />

      <v-textarea
        v-model="comment"
        :rules="[rules.minLength, rules.maxLength]"
        :label="$t('host.controlAccess.singlePayment.form.observations')"
        outlined
      />

      <div class="d-flex justify-end">
        <v-btn
          @click="openedForm = false"
          :disabled="loading"
          color="grey darken-2"
          class="mr-3"
          text
        >
          {{ $t('host.history.cancel') }}
        </v-btn>

        <v-btn
          @click="validate"
          :loading="loading"
          color="primary"
          elevation="0"
          :disabled="isLoading || !valid"
        >
          {{ $t('host.history.addObservation') }}
        </v-btn>
      </div>
    </v-form>

    <v-divider class="my-8" />

    <v-data-table
      :headers="headers"
      :items="hostHistoryList"
      :no-data-text="$t('appointment.table.noData')"
      :loading-text="$t('appointment.table.loading')"
      :items-per-page="10"
      :footer-props="{
        'items-per-page-text': '',
        'items-per-page-options': [],
      }"
      mobile-breakpoint="20"
      class="elevation-0"
      single-expand
      disable-sort
      show-expand
    >
      <template v-slot:[`item.date_created`]="{ item }">
        {{ item.date_created ? formatDate(item.date_created) : '---' }}
      </template>
      <template v-slot:[`item.author`]="{ item }">
        <span>
          {{ item.author ? item.author.name : '---' }}
        </span>
      </template>
      <template v-slot:[`item.action`]="{ item }">
        {{ item.action ? $t(`host.history.action.${item.action}`) : '---' }}
      </template>

      <template v-slot:expanded-item="{ headers, item }">
        <td :colspan="headers.length" class="pa-0 ma-0">
          <div class="pa-5 grey lighten-4 comment mw-700">
            <h6 class="text-uppercase grey--text mb-1">
              {{ $t('host.label.description') }}
            </h6>
            {{ item.comment ? item.comment : '---' }}
          </div>
        </td>
      </template>
    </v-data-table>

    <h-pagination
      v-if="hostHistoryList.length >= 1"
      :fetch="() => fetchHostHistoryList(this.currentHostId)"
    />
  </div>
</template>

<script>
import { mapActions, mapGetters } from 'vuex'
import { catalogDate } from '@/util/catalog'
import { hostHistoryHeaders } from '@/views/listHeaders/hostHeaders'
import { required, minLength } from '@/util/errorHandling'
import { VMoney } from 'v-money'

import HAmount from '../app/HAmount'
import HTextInput from '../app/HTextInput'
import CopyToClipboard from '@/components/app/CopyToClipboard'

export default {
  name: 'HostHistory',

  components: {
    HAmount,
    HTextInput,
    CopyToClipboard,
  },

  directives: {
    money: VMoney
  },

  props: {
    hostId: {
      type: [String, Number],
      required: false,
    },
  },

  watch: {
    hostId(id) {
      if (id) this.fetchHostHistoryList(id)
      else if (this.currentHostId) this.fetchHostHistoryList(this.currentHostId)
    },

    openedForm() {
      if (!this.openedForm) {
        this.comment = ''
        this.action = null
        this.valid = false
      }
    }
  },

  data() {
    return {
      action: null,
      valid: false,
      loading: false,
      openedForm: false,
      comment: '',
      headers: hostHistoryHeaders,
      formatDate: catalogDate,
      rules: {
        required,
        minLength,
        maxLength: (v) => v.length <= 255 || this.$t('host.history.error.maxLength', { maxLength: 255}),
      },

      actions: [
        { text: this.$t('host.history.action.PRAISE'), value: 'PRAISE' },
        { text: this.$t('host.history.action.COMPLAINT'), value: 'COMPLAINT' },
        { text: this.$t('host.history.action.NOTE'), value: 'NOTE' },
        { text: this.$t('host.history.action.RESTRICTION'), value: 'RESTRICTION' },
      ]
    }
  },

  computed: {
    ...mapGetters(['hostHistoryList', 'isLoading', 'host']),

    currentHostId() {
      return this.host.id ?? this.hostId
    },
  },

  methods: {
    ...mapActions(['fetchHostHistoryList', 'createHostHistory']),

    validate() {
      if (!this.$refs.addObservationForm.validate()) return

      this.loading = true

      const body = {
        host_id: this.currentHostId,
        comment: this.comment,
        action: this.action,
      }

      Promise.resolve(this.createHostHistory(body))
        .then(() => {
          this.openedForm = false
          this.comment = ''
          this.action = null
          this.valid = false
        })
        .finally(() => {
          this.loading = false
        })
    },
  },

  async mounted() {
    if (this.currentHostId) {
      await this.fetchHostHistoryList(this.currentHostId)
    }
  },
}
</script>

<style scoped>
.comment {
  word-wrap: break-word;
  overflow-wrap: break-word;
}
</style>
